import type { UpdatePrivateEventInput } from '../../api/private-event-update';
import type { PrivateEventDetail } from '../../api/privateEvents';

import { daysBetween, shiftDate } from './private-event-dates';
import type { PrivateEventFormFields as Fields } from './private-event-form-state';
import { buildLocationInput } from './private-event-location-input';

function locationSignature(f: Fields): string {
  if (f.locationMode === 'venue') return `venue|${f.venueId}`;
  if (f.locationMode === 'tbd') return `tbd|${f.regionId}|${f.timeZone}`;
  const address = [f.locationName, f.addressLine1, f.addressLine2, f.city, f.state, f.zipCode];
  return ['client', f.regionId, f.timeZone, ...address.map((x) => x.trim())].join('|');
}

/** An imported "not recorded" location has no form value of its own, so the
 *  control being touched is the only sign the RD means to set one. */
function locationChanged(event: PrivateEventDetail, seed: Fields, now: Fields, touched: boolean) {
  if (!touched) return false;
  return event.locationKind === 'unrecorded' || locationSignature(seed) !== locationSignature(now);
}

/** The end keeps its distance from the start date, so an overnight event stays
 *  overnight when it moves. */
export function movedEndDate(seed: Fields, now: Fields): string {
  if (!seed.endDate) return now.date;
  return shiftDate(seed.endDate, daysBetween(seed.date, now.date));
}

/** Only what the RD changed in When & what. The server re-derives the instants
 *  when the zone changes, so only a moved wall clock sends a time. */
export function whenWhatPatch(
  event: PrivateEventDetail,
  seed: Fields,
  now: Fields,
  locationTouched: boolean,
): UpdatePrivateEventInput {
  const patch: UpdatePrivateEventInput = {};
  const dateMoved = now.date !== seed.date;
  const endDate = dateMoved ? movedEndDate(seed, now) : now.endDate;
  if (dateMoved || now.startTime !== seed.startTime) {
    patch.startsAt = `${now.date}T${now.startTime}:00`;
  }
  if (dateMoved || now.endTime !== seed.endTime || endDate !== seed.endDate) {
    patch.endsAt = `${endDate}T${now.endTime}:00`;
  }
  if (now.workshopTypeId !== seed.workshopTypeId) patch.workshopTypeId = now.workshopTypeId;
  if (now.headcount !== seed.headcount) patch.headcount = Number(now.headcount);
  if (locationChanged(event, seed, now, locationTouched))
    patch.location = buildLocationInput(now, { addressLine2: true });
  return patch;
}
